<template>
  <d-container
    paddingless-mobile
    :fluid="fluid"
    :no-padding="noPadding"
  >
    <div :class="{ 'columns is-multiline': filters }">
      <div
        :class="{
          'is-9-widescreen list column is-12-desktop is-12-tablet': filters,
        }"
      >
        <div
          v-loading="loading && firstLoad"
          class="data-table-data-wrapper"
        >
          <div class="card pt-4 pr-4 pl-4">
            <slot
              name="header"
              :search="search"
              :onDebounceSearch="onDebounceSearch"
              :searchable="searchable"
            >
              <div
                v-if="searchable"
                class="mb-2 is-flex-desktop is-flex-direction-row is-justify-content-flex-start is-1"
              >
                <b-field class="is-flex is-flex-grow-1 mr-2">
                  <b-input
                    :value="search"
                    class="is-flex-grow-1"
                    :placeholder="`${$t('search')}...`"
                    type="text"
                    icon="magnify"
                    @input="onDebounceSearch"
                  />
                </b-field>
                <slot
                  name="slide-filters"
                  class="is-full"
                />
              </div>
            </slot>
            <b-table
              v-if="data != null"
              ref="b-table"
              :data="items"
              :bordered="false"
              :striped="striped"
              :scrollable="scrollable"
              :narrowed="false"
              :hoverable="false"
              :focusable="false"
              :mobile-cards="true"
              :loading="loading"
              :detailed="detailed"
              :height="height"
              :show-button="showButton"
              :paginated="isPaginated"
              backend-pagination
              :pagination-position="paginationPosition"
              :current-page="currentPage"
              :total="total"
              :per-page="perPage"
              backend-sorting
              :sticky-header="stickyHeader"
              :default-sort="[sortField, sortOrder]"
              @page-change="onPageChange"
              @sort="onSort"
              @click="onClick"
            >
              <slot />

              <b-table-column
                v-slot="props"
                :visible="
                  editButton || deleteButton || $scopedSlots.actions != null
                "
                cell-class="actions"
              >
                <div class="has-text-right is-flex is-justify-content-flex-end">
                  <slot
                    name="actions"
                    :row="props.row"
                  />

                  <router-link
                    v-if="showButton"
                    :to="showUrl(props.row)"
                    tag="button"
                    class="button is-small is-white"
                  >
                    <div class="icon has-text-grey">
                      <i class="mdi mdi-eye mdi-18px" />
                    </div>
                  </router-link>

                  <router-link
                    v-if="editButton"
                    :to="editUrl(props.row)"
                    :data-url="editUrl(props.row)"
                    :disabled="!canUpdate(props.row)"
                    tag="button"
                    class="button is-small is-white"
                  >
                    <div class="icon has-text-grey">
                      <i class="mdi mdi-cog mdi-18px" />
                    </div>
                  </router-link>

                  <d-delete
                    v-if="deleteButton"
                    :url="deleteUrl(props.row)"
                    class="button is-small is-white"
                    :disabled="!canDelete(props.row)"
                    @success="onDelete(props.row)"
                  >
                    <div class="icon has-text-grey">
                      <i class="mdi mdi-delete mdi-18px" />
                    </div>
                  </d-delete>

                  <slot
                    name="customDelete"
                    :row="props.row"
                  />
                </div>
              </b-table-column>

              <template
                slot="detail"
                slot-scope="props"
              >
                <slot
                  name="detail"
                  v-bind="props"
                />
              </template>

              <template slot="empty">
                <section class="hero">
                  <div class="hero-body has-text-grey has-text-centered">
                    <img
                      src="/img/empty-states/data.svg"
                      alt="No data"
                      width="100"
                      class="mb-20 is-inline-block"
                    >
                    <p>{{ $t("no_data") }}</p>
                  </div>
                </section>
              </template>
            </b-table>
            <div
              v-if="exportable"
              class="data-table-export"
            >
              <a
                :href="exportUrl"
                class="button is-text"
              >
                <span class="icon">
                  <i class="mdi mdi-table-arrow-right" />
                </span>
                <span>{{ $t("export_data") }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <slot
        name="filters"
        class="column card"
      />
    </div>
  </d-container>
</template>

<script>
export default {
  props: {
    resource: {
      required: true,
      type: String,
    },
    showButton: {
      default: false,
      type: Boolean,
    },
    editButton: {
      default: true,
      type: Boolean,
    },
    deleteButton: {
      default: true,
      type: Boolean,
    },
    searchable: {
      default: false,
      type: Boolean,
    },
    striped: {
      default: false,
      type: Boolean,
    },
    filters: {
      default: false,
      type: Boolean,
    },
    scrollable: {
      default: false,
      type: Boolean,
    },
    routeKeyName: {
      default() {
        return "id";
      },
      type: String,
    },
    url: {
      default() {
        return this.$laravel.route(`api.${this.resource}.index`);
      },
      type: String,
    },
    showUrl: {
      default(row) {
        return `${this.resource}/${row[this.routeKeyName]}`;
      },
      type: Function,
    },
    editUrl: {
      default(row) {
        return `${this.resource}/${row[this.routeKeyName]}/edit`;
      },
      type: Function,
    },
    deleteUrl: {
      default(row) {
        return `${this.url}/${row[this.routeKeyName]}`;
      },
      type: Function,
    },
    detailed: {
      default: false,
      type: Boolean,
    },
    isPaginated: {
      default: true,
      type: Boolean,
    },
    paginationPosition: {
      default: "bottom",
      type: String,
    },
    defaultSortField: {
      default: null,
      type: String,
    },
    defaultSortOrder: {
      default: null,
      type: String,
    },
    fluid: {
      default: false,
      type: Boolean,
    },
    noPadding: {
      default: false,
      type: Boolean,
    },
    parameters: {
      default() {
        return {};
      },
      type: Object,
    },
    stickyHeader: {
      default: false,
      type: Boolean,
    },
    height: {
      default: undefined,
      type: Number,
    },
    exportable: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      data: null,
      page: this.parameters.page || 1,
      sortField: this.defaultSortField,
      sortOrder: this.defaultSortOrder,
      search: this.parameters.search || "",
      firstLoad: true,
      loading: true,
    };
  },
  computed: {
    items() {
      if (!Object.prototype.hasOwnProperty.call(this.data, "data")) {
        return this.data;
      }
      return this.data.data;
    },
    isLaravelResource() {
      return Object.prototype.hasOwnProperty.call(this.data, "meta");
    },
    perPage() {
      if (this.isLaravelResource) {
        return this.data.meta.per_page;
      }
      return this.data.per_page;
    },
    total() {
      if (this.isLaravelResource) {
        return this.data.meta.total;
      }
      return this.data.total;
    },
    currentPage() {
      if (this.isLaravelResource) {
        return this.data.meta.current_page;
      }
      return this.data.current_page;
    },
    debounceLoadData() {
      return _.debounce(function () {
        this.loadData();
      }, 800);
    },
    allParameters() {
      let sortField = this.sortField;

      if (this.sortOrder && this.sortOrder.toLowerCase() == "desc") {
        sortField = "-" + sortField;
      }

      let internalParameters = {
        page: this.page,
        sort: sortField,
        search: this.search,
      };

      // Remove null and undefined (https://stackoverflow.com/a/57625661)
      internalParameters = Object.entries(internalParameters).reduce(
        (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
        {}
      );

      return _.merge(_.cloneDeep(this.parameters), internalParameters);
    },
    exportUrl() {
      const url = new URL(this.url);
      const params = this.allParameters;

      Object.entries(params).forEach((item) => {
        const key = item[0];
        const value = item[1];

        if (["sort", "direction"].includes(key)) {
          return;
        }

        if (value === null || value == undefined) {
          return;
        }

        url.searchParams.append(key, value);
      });

      url.searchParams.append("format", "csv");

      return url;
    },
  },
  watch: {
    parameters: {
      handler() {
        this.debounceLoadData();
      },
      deep: true,
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;

      this.$http
        .get(this.url, {
          params: this.allParameters,
        })
        .then((response) => {
          this.data = response.data;
          this.loading = false;
          this.firstLoad = false;
        })
        .catch((error) => {
          this.loading = false;
        });
    },
    onPageChange(page) {
      this.page = page;
      this.$emit("pagination", page);
      this.loadData();
    },
    onSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.page = 0;
      this.loadData();
    },
    onSearch() {
      this.loadData();
    },
    onDebounceSearch: _.debounce(function (keywords) {
      this.search = keywords;
      this.$emit("search", keywords);
      this.loadData();
    }, 800),
    onClick(payload) {
      this.$emit("click", payload);
    },
    canUpdate(row) {
      return this.gate(row, "update");
    },
    canDelete(row) {
      return this.gate(row, "delete");
    },
    onDelete(row) {
      this.$emit("delete", row);
      this.loadData();
    },
    gate(row, action) {
      if (row.can && Object.prototype.hasOwnProperty.call(row.can, action)) {
        return row.can[action];
      }
      return true;
    },
  },
};
</script>
